import {
  Cartesian3,
  ConstantProperty,
  Entity,
  HorizontalOrigin,
  VerticalOrigin,
  Viewer,
} from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import CameraControlsPlugin from "./CameraControlsPlugin";

export default class OrbitPointVisualizerPlugin
  implements AttachablePluginInterface
{
  private static readonly INITIAL_MARKER_SIZE = 0.2;

  private _viewer: Viewer = null;
  private _markerEntity: Entity;
  private _markerSize: number = OrbitPointVisualizerPlugin.INITIAL_MARKER_SIZE;
  private _animationTime: number = 0;
  private _detachTickHandlerFunc: () => void;

  constructor(private readonly _cameraControlsPlugin: CameraControlsPlugin) {}

  setViewer(viewer: Viewer) {
    this._viewer = viewer;

    this._detachTickHandlerFunc?.();
    this._detachTickHandlerFunc = viewer.clock.onTick.addEventListener(
      this.tick.bind(this),
    );
  }

  detachViewer() {
    this._viewer = null;
    this._detachTickHandlerFunc?.();
    this._detachTickHandlerFunc = null;
  }

  private tick() {
    if (this._cameraControlsPlugin.isOrbiting) {
      if (!this._markerEntity) {
        const pivot = this._cameraControlsPlugin.orbitPivot;
        if (pivot) this.addMarker(pivot);
      }
      if (this._cameraControlsPlugin.isMoving && this._markerSize <= 0)
        this._markerSize = OrbitPointVisualizerPlugin.INITIAL_MARKER_SIZE;
    } else if (this._markerEntity) {
      this.removeMarker();
    }
  }

  private addMarker(position: Cartesian3) {
    this._markerEntity = this._viewer.entities.add({
      position,
      billboard: {
        image: "/images/billboards/orbit-marker.png",
        scale: this._markerSize,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        verticalOrigin: VerticalOrigin.CENTER,
        horizontalOrigin: HorizontalOrigin.CENTER,
      },
      properties: {
        excludeFromPicking: true,
      },
    });

    requestAnimationFrame(this.animateMarker.bind(this));
  }

  private removeMarker() {
    const wasRemoved = this._viewer.entities.remove(this._markerEntity);
    if (!wasRemoved) {
      console.warn("Unable to remove the orbit marker entity from the viewer.");
      return;
    }

    this._markerEntity = null; // needs to be set after removing the entity from the viewer
    this._markerSize = OrbitPointVisualizerPlugin.INITIAL_MARKER_SIZE;
    this._viewer.scene.requestRender();
    this._animationTime = 0;
  }

  private animateMarker(timestamp: number) {
    /**
     * 15 is an arbitrary divisor to control the animation speed.
     * Higher values will slow it down, lower values will speed it up.
     */
    const animationDivisor = 15;
    const elapsedTime = this._animationTime
      ? (timestamp - this._animationTime) / animationDivisor
      : 0;

    this._markerSize -= 0.003 * elapsedTime;

    if (this._markerSize <= 0) {
      this._markerSize = 0;
    }

    if (!this._markerEntity) return;
    this._markerEntity.billboard.scale = new ConstantProperty(this._markerSize);
    this._viewer.scene.requestRender();
    requestAnimationFrame(this.animateMarker.bind(this));
    this._animationTime = timestamp;
  }
}
